import { recruitingAPI } from "../../services/recruitingApi";
import {
  createPersonnelAssignmentHook,
  type UsePersonnelAssignmentOptions,
  type AssignedPerson,
  type AvailablePerson,
  type NotificationState,
  type ConfirmDialogState,
} from "./usePersonnelAssignment";

export interface AssignedCandidateKam extends AssignedPerson {
  kam_user_id: number;
  kam_name?: string;
}

export type AvailableCandidateKam = AvailablePerson;

const usePersonnelAssignment =
  createPersonnelAssignmentHook<AssignedCandidateKam>({
    roleSlug: "sales",
    assignedQueryKey: "candidate-kams",
    availableQueryKey: "sales-list",
    assignedDataKey: "assigned_kams",
    fetchAssigned: (candidateId) =>
      recruitingAPI.getKamsForCandidate(candidateId),
    assign: (candidateId, kamUserId) =>
      recruitingAPI.assignKamToCandidate(candidateId, kamUserId),
    remove: (candidateId, kamUserId) =>
      recruitingAPI.removeKamFromCandidate(candidateId, kamUserId),
    label: "KAM",
    gateAssignedOnPermission: false,
    getPersonnelId: (k) => k.kam_user_id,
  });

export interface UseCandidateKamAssignmentReturn {
  kams: AssignedCandidateKam[];
  availableKams: AvailableCandidateKam[];
  selectedKamId: string;
  setSelectedKamId: (id: string) => void;
  isAssigningKam: boolean;
  handleAssignKam: (overrideId?: string) => Promise<void>;
  handleRemoveKam: (kamUserId: number) => void;
  canManageKams: boolean;
  notification: NotificationState;
  closeNotification: () => void;
  confirmDialog: ConfirmDialogState;
  confirmRemoval: () => void;
  cancelRemoval: () => void;
}

/**
 * Custom hook for managing KAM (sales) assignments for a candidate.
 * Wraps the shared usePersonnelAssignment hook with KAM-specific naming.
 */
export function useCandidateKamAssignment(
  options: UsePersonnelAssignmentOptions,
): UseCandidateKamAssignmentReturn {
  const result = usePersonnelAssignment(options);

  return {
    kams: result.assigned,
    availableKams: result.available,
    selectedKamId: result.selectedId,
    setSelectedKamId: result.setSelectedId,
    isAssigningKam: result.isAssigning,
    handleAssignKam: result.handleAssign,
    handleRemoveKam: result.handleRemove,
    canManageKams: result.canManage,
    notification: result.notification,
    closeNotification: result.closeNotification,
    confirmDialog: result.confirmDialog,
    confirmRemoval: result.confirmRemoval,
    cancelRemoval: result.cancelRemoval,
  };
}

export default useCandidateKamAssignment;
